import {Draggable, gsap, InertiaPlugin, Flip} from "gsap/all";
import $ from "jquery";
import {BaseGameMars} from "../base/BaseGameMars";
import {Game} from "./Game";
import {BackgroundMorpher} from "./BackgroundMorpher";
import {PathToMarsScreenTransition} from "./PathToMarsScreenTransition";

// Constructor
export class PathToMars extends BaseGameMars {

  constructor() {
    super(`data/path-to-mars.json`, 'path-to-mars');
    gsap.registerPlugin(Draggable, InertiaPlugin, Flip);

    // The "try again" fade out, rebuild and fade in, tracked so a reset landing
    // in the middle of it can cancel the rest.
    this.tryAgainTimeline = null;
  }

  initGame() {
    // Always-visible background that morphs shape per screen. 0.4s duration
    // matches the base screen fade-out so the morph completes before fade-in.
    this.backgroundMorpher = new BackgroundMorpher(this, {duration: 2});
    // Set the first screen's shape instantly so it doesn't morph in on load.
    this.backgroundMorpher.to("rect", {instant: true});

    // Drives screen transitions; morphs the background as part of each one.
    this.screenTransition = new PathToMarsScreenTransition(this.backgroundMorpher);

    this.game = new Game(this);

    super.initGame();
  }

  // Curtain transition. The screens are clip-masked to the background shape, so
  // collapsing the shape hides the content. Sequence: close the curtain, then
  // (only once fully closed) swap the screen *instantly* — no content fade — then
  // reopen the curtain to the new screen's shape. We claim the lock/target
  // guards here and tell super to skip them, since it runs inside the close
  // callback rather than up front.
  goToScreen(screenId, callback = null, skipLockScreenCheck = false, options = {}) {
    // Initial reveal (boot, no screen shown yet): no curtain — the shape is
    // already set instantly in initGame, just let the base reveal screen 1.
    if (this.currentScreen === 0) {
      return super.goToScreen(screenId, callback, skipLockScreenCheck, options);
    }

    // Same guards the base applies, evaluated up front (before we start
    // closing), including the option letting a screen transition to itself.
    // A reset from a later step of screen 1 asks for exactly that.
    if (!skipLockScreenCheck && this.lockScreen) return false;
    if (!options.allowSameScreenTransition && this.targetScreen === screenId) return;

    this.targetScreen = screenId;
    this.lockInteraction();

    const shape = this.screenTransition.shapeForScreen(screenId);
    const half = this.backgroundMorpher.duration / 2;
    const hold = this.backgroundMorpher.closedHold;
    const $leaving = this.$currentScreen;

    // 1) Close the curtain, fading the leaving content out in lockstep so
    //    nothing shows through the thin closed slit.
    if ($leaving && $leaving.length) {
      gsap.to($leaving, {
        alpha: 0,
        duration: half,
        ease: this.backgroundMorpher.closeEase
      });
    }
    this.backgroundMorpher.close(() => {
      // 2) Curtain is shut — swap the screen with no fade (skipOut/skipIn) and
      //    bypass the guards we already claimed above.
      super.goToScreen(screenId, () => {
        // Base unlocks at the end of its (instant) swap; keep it locked while
        // the curtain reopens.
        this.lockInteraction();
        // Keep the new content hidden through the closed + hold phase, then
        // fade it in as the curtain reopens (so it's not visible in the slit).
        const $entering = this.$currentScreen;
        gsap.set($entering, {alpha: 0});
        gsap.to($entering, {
          alpha: 1,
          duration: half,
          delay: hold,
          ease: this.backgroundMorpher.openEase
        });
        // 3) Reopen the curtain to the new screen's shape.
        this.backgroundMorpher.open(shape, () => {
          this.unlockInteraction();
          if (typeof callback === "function") callback();
        });
      }, true, {skipOut: true, skipIn: true, allowSameScreenTransition: true});
    });

    return true;
  }

  initScreen(screenId) {
    const targetScreenNoSuffix = parseInt(screenId.toString().split("-")[0]);

    if (targetScreenNoSuffix === 1) {
      this.resetBackgroundOrbit();
    } else if (targetScreenNoSuffix === 2) {
      // TODO Do something for screen 2, if required
      this.game.start();
    } else if (targetScreenNoSuffix === 3) {
      // TODO Do something for screen 3, if required
    }
  }

  // The orbit illustration at rest, where the idle screen shows it. Called from
  // initScreen, which always runs while screen 1 is still hidden (behind the
  // closed curtain on a reset, before the first reveal on boot), so this is the
  // only place the resting transform is written and it is never seen landing.
  // The y offset pairs with top: 50% and stays put, the step 2 tween below only
  // moves the two others.
  resetBackgroundOrbit() {
    const $orbit = $("#screen-1 .background-orbit");
    if (!$orbit.length) return;

    gsap.killTweensOf($orbit);
    gsap.set($orbit, {
      xPercent: 0,
      yPercent: -50,
      scale: 0.85
    });
  }

  beforeStep(stepId) {
    // Step 2 brings the orbit to the middle of the screen, where the text modal
    // sits over it.
    if (this.currentScreen === 1 && stepId === 2) {
      gsap.to(this.$currentScreen.find(".background-orbit"), {
        xPercent: -50,
        scale: 1,
        duration: 1,
        ease: "power1.inOut"
      });
    }
  }

  initStep(stepId) {
    if (this.currentScreen === 2) {
      this.game.initStep(stepId);
    }
  }

  afterStep(stepId) {
    if (this.currentScreen === 2) {
      this.game.afterStep(stepId);
    }
  }

  // "Try again" from a conclusion, and "Restart game" from the frame. Staying on
  // the same screen rules out reset()/goToScreen (the same-screen guard bails out),
  // so we fade the game out, rebuild it from scratch on the spot, then fade it back
  // in. No jump, since the solar system and gauges reset while hidden. It comes
  // back on step 2 (hit the launch window), since the player has already been
  // through the "Ready for launch" step once.
  tryAgain() {
    if (this.lockScreen) return;
    this.lockInteraction();

    const $game = this.$currentScreen.find(".game");

    this.killTimeline(this.tryAgainTimeline);
    this.tryAgainTimeline = gsap.timeline({
      onComplete: () => {
        this.tryAgainTimeline = null;
      }
    });

    this.tryAgainTimeline.to($game, {
      autoAlpha: 0,
      duration: 0.4,
      ease: "power1.out"
    });

    // Same pieces a fresh screen entry runs, but with step 2 current: a clean game
    // (solar system rebuilt, gauges reset, handlers rebound), then the targets and
    // ship step 2 brings in. Visibility is hidden here, so it lays out without a
    // flash.
    this.tryAgainTimeline.add(() => {
      this.setupScreenSteps(2);
      this.game.start({
        showFrame: false,
        cycleProgress: this.game.tryAgainCycleProgress
      });
      this.initStep(2);
    });

    this.tryAgainTimeline.to($game, {
      autoAlpha: 1,
      duration: 0.4,
      ease: "power1.out"
    });

    this.tryAgainTimeline.add(() => {
      // The fade on the game suppressed the target lenses' glass, which doesn't
      // come back on its own.
      this.game.refreshGlass($(".planet-target-lens"));
      this.unlockInteraction();
      this.afterStep(2);
    });

    // Show the top bar again
    gsap.set($(".top-bar"), {alpha: 1});

  }

  /**
   * Reset function, used to go back clean version of the game
   * @param screenId
   */
  reset(screenId) {
    // A reset can land mid "try again": cancel the rest of it, or it would restart
    // the game and accept presses on a screen that is being left.
    this.killTimeline(this.tryAgainTimeline);
    this.tryAgainTimeline = null;

    this.game.stop();

    // Reset variable and timeline
    this.killTimeline(this.currentTimeline);

    const resetCallback = () => {
      // TODO Things that reset AFTER goToScreen

      // A "try again" cut short can leave the game faded out. Screen 2 is hidden
      // by now, so bring it back for the next visitor without it being seen.
      gsap.set($("#screen-2 .game"), {clearProps: "opacity,visibility"});

      this.game.reset();
    }

    // Super
    return super.reset(screenId, resetCallback);
  }

}
